import type { SearchProvider, SearchResult } from './search-provider';
import { queryKeys } from '@vtak/shared';
import { queryClient } from '@/queries/query-client';
import { authHeaders } from '@/services/api-client';

/** Zoom applied when flying to a geocoded address/place. */
const GEOCODE_ZOOM = 17;

/** Minimum query length before calling the geocoder. */
const MIN_QUERY_LENGTH = 3;

/** Geocoded places are effectively static — cache repeats for 5 min. */
const GEOCODE_STALE_MS = 300_000;

/** One geocoded place returned by the server proxy. */
interface GeocodeRow {
  label: string;
  sublabel: string;
  lat: number;
  lon: number;
}

/**
 * Address/place search via the server's `/api/geocode` proxy (OpenStreetMap /
 * Nominatim). Results carry a resolved coordinate, so a pick flies directly with
 * no second round-trip. Identical queries are served from the shared Query cache
 * (dedup + staleTime); degrades silently (returns []) on any failure.
 */
export const geocodeProvider: SearchProvider = {
  id: 'geocode',
  search: async (query) => {
    const trimmed = query.trim();
    if (trimmed.length < MIN_QUERY_LENGTH) return [];
    try {
      const rows = await queryClient.fetchQuery({
        queryKey: queryKeys.geocode(trimmed),
        staleTime: GEOCODE_STALE_MS,
        queryFn: () => fetchGeocode(trimmed),
      });
      return rows.map(toResult);
    } catch {
      return [];
    }
  },
};

/** Fetch geocode rows from the server proxy (throws on failure so it isn't cached). */
async function fetchGeocode(term: string): Promise<GeocodeRow[]> {
  const res = await fetch(`/api/geocode?q=${encodeURIComponent(term)}`, authHeaders());
  if (!res.ok) throw new Error(`geocode request failed: ${res.status}`);
  return (await res.json()) as GeocodeRow[];
}

/** Map a geocode row to a SearchResult. */
function toResult(row: GeocodeRow): SearchResult {
  return { label: row.label, sublabel: row.sublabel, lat: row.lat, lon: row.lon, source: 'Address', zoom: GEOCODE_ZOOM };
}
